import { useMutation } from "@tanstack/react-query";
import * as React from "react";

import {
  ApiError,
  updateDealDraft,
  type Deal,
  type DealDraftUpdatePayload,
  type ServiceOffering,
} from "@/api/client";
import { useCurrentUser } from "@/auth/useCurrentUser";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { DateRowInput } from "@/components/ui/date-row-input";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { FormError } from "@/components/ui/form-error";

import { decode422 } from "./deal422";

interface Props {
  deal: Deal;
  onComplete: (deal: Deal) => void;
  onBack: () => void;
  /** Wizard position (CHP-368: 3 when an Assign-Deal-Owner step precedes the
   * flow; 2 otherwise). Drives the card heading. */
  stepNumber?: number;
  /** CHP-474 cross-step jump: a server field name to highlight on mount, set by
   * the wizard when a later step's submit failed validation on a field this
   * step owns (so the user lands on the offending input). */
  initialErrorField?: string | null;
}

const SERVICE_OPTIONS: { value: ServiceOffering; label: string }[] = [
  { value: "BMaaS", label: "BMaaS (Bare Metal as a Service)" },
];

const TERM_OPTIONS = [12, 24, 36, 48, 60] as const;

// CHP-781: form (DOM) order of the highlightable fields, so when several error
// at once we scroll/focus the FIRST one top-to-bottom.
const FIELD_FOCUS_ORDER = [
  "service_offering",
  "primary_use_case",
  "total_contract_value",
  "proposed_term_months",
  "target_close_date",
  "estimated_gpu_count",
] as const;

/** Convert cents to dollars for display. Returns empty string for null. */
function centsToDollars(cents: number | null): string {
  if (cents == null) return "";
  return (cents / 100).toFixed(2);
}

/** Convert dollars string to cents. Returns null on invalid input. */
function dollarsToCents(dollars: string): number | null {
  const n = Number(dollars);
  if (!Number.isFinite(n) || n < 0) return null;
  return Math.round(n * 100);
}

export function DealWizardStep2({
  deal,
  onComplete,
  onBack,
  stepNumber = 2,
  initialErrorField = null,
}: Props) {
  const { accessToken } = useCurrentUser();
  const [serviceOffering, setServiceOffering] = React.useState<ServiceOffering | "">(
    deal.service_offering ?? "BMaaS",
  );
  const [primaryUseCase, setPrimaryUseCase] = React.useState(deal.primary_use_case ?? "");
  const [contractValueDollars, setContractValueDollars] = React.useState(
    centsToDollars(deal.total_contract_value),
  );
  const [termMonths, setTermMonths] = React.useState<string>(
    deal.proposed_term_months?.toString() ?? "",
  );
  const [targetCloseDate, setTargetCloseDate] = React.useState(deal.target_close_date ?? "");
  const [estimatedGpuCount, setEstimatedGpuCount] = React.useState(
    deal.estimated_gpu_count?.toString() ?? "",
  );
  const [infraNotes, setInfraNotes] = React.useState(deal.infrastructure_notes ?? "");
  const [error, setError] = React.useState<string | null>(null);
  // CHP-781: every invalid field at once, keyed by field name -> inline message.
  // CHP-474: aria-invalid + scroll/focus the FIRST errored field. Seeded from a
  // cross-step jump so the field highlights when this step mounts.
  const [errorFields, setErrorFields] = React.useState<Record<string, string>>(() =>
    initialErrorField ? { [initialErrorField]: "" } : {},
  );

  // Refs for scroll-into-view + focus when a field errors (CHP-474 idiom from
  // DealDashboardHeader). Ordered top-to-bottom so CHP-781 focuses the FIRST.
  const serviceRef = React.useRef<HTMLSelectElement>(null);
  const useCaseRef = React.useRef<HTMLTextAreaElement>(null);
  const tcvRef = React.useRef<HTMLInputElement>(null);
  const termRef = React.useRef<HTMLSelectElement>(null);
  const closeDateRef = React.useRef<HTMLInputElement>(null);
  const gpuRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    const firstField = FIELD_FOCUS_ORDER.find((f) => f in errorFields);
    if (!firstField) return;
    const el =
      firstField === "service_offering"
        ? serviceRef.current
        : firstField === "primary_use_case"
          ? useCaseRef.current
          : firstField === "total_contract_value"
            ? tcvRef.current
            : firstField === "proposed_term_months"
              ? termRef.current
              : firstField === "target_close_date"
                ? closeDateRef.current
                : firstField === "estimated_gpu_count"
                  ? gpuRef.current
                  : null;
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    window.setTimeout(() => el.focus(), 150);
  }, [errorFields]);

  const update = useMutation<Deal, ApiError, DealDraftUpdatePayload>({
    mutationFn: (payload) => updateDealDraft(accessToken!, deal.id, payload),
    onSuccess: (updated) => {
      onComplete(updated);
    },
    onError: (e) => {
      if (e.status === 422) {
        const detail =
          e.body && typeof e.body === "object" && "detail" in e.body
            ? (e.body as { detail?: unknown }).detail
            : undefined;
        const decoded = decode422(detail);
        setError(decoded.message);
        setErrorFields(decoded.field ? { [decoded.field]: decoded.message } : {});
      } else {
        setErrorFields({});
        setError("Couldn't save progress. Please try again.");
      }
    },
  });

  function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    setErrorFields({});

    // CHP-781: validate EVERY required field and collect all failures, so the
    // user sees every missing/invalid field highlighted at once on submit.
    const fieldErrors: Record<string, string> = {};

    if (!serviceOffering) {
      fieldErrors.service_offering = "Service offering is required.";
    }

    if (!primaryUseCase.trim()) {
      fieldErrors.primary_use_case = "Primary use case is required.";
    }

    const cents = dollarsToCents(contractValueDollars);
    if (!contractValueDollars) {
      fieldErrors.total_contract_value = "Total contract value is required.";
    } else if (cents == null || cents <= 0) {
      fieldErrors.total_contract_value = "Please enter a valid contract value greater than zero.";
    }

    const term = Number(termMonths);
    if (!termMonths) {
      fieldErrors.proposed_term_months = "Proposed term is required.";
    } else if (![12, 24, 36, 48, 60].includes(term)) {
      fieldErrors.proposed_term_months =
        "Proposed term must be one of 12, 24, 36, 48 or 60 months.";
    }

    if (!targetCloseDate) {
      fieldErrors.target_close_date = "Target close date is required.";
    } else if (new Date(targetCloseDate) <= new Date()) {
      fieldErrors.target_close_date = "Target close date must be in the future.";
    }

    if (Object.keys(fieldErrors).length > 0) {
      setErrorFields(fieldErrors);
      const firstField = FIELD_FOCUS_ORDER.find((f) => f in fieldErrors);
      setError(firstField ? (fieldErrors[firstField] ?? null) : null);
      return;
    }

    // No field errors → cents/term/serviceOffering are valid (the checks above
    // guarantee it); narrow the unions for the type-checker.
    if (cents == null || !serviceOffering) return;

    const payload: DealDraftUpdatePayload = {
      service_offering: serviceOffering,
      primary_use_case: primaryUseCase.trim(),
      total_contract_value: cents,
      proposed_term_months: term,
      target_close_date: targetCloseDate,
    };
    if (estimatedGpuCount) {
      const gpu = Number(estimatedGpuCount);
      if (Number.isFinite(gpu) && gpu > 0) {
        payload.estimated_gpu_count = Math.round(gpu);
      }
    }
    if (infraNotes.trim()) payload.infrastructure_notes = infraNotes.trim();

    update.mutate(payload);
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Step {stepNumber}: Opportunity details</CardTitle>
      </CardHeader>
      <CardContent>
        {/* CHP-781: noValidate so OUR JS validation flags every missing required
            field at once instead of native HTML blocking on the first. */}
        <form noValidate onSubmit={onSubmit} className="grid gap-4">
          <div className="grid gap-2">
            <Label htmlFor="wiz-service">Service offering *</Label>
            <select
              id="wiz-service"
              ref={serviceRef}
              value={serviceOffering}
              onChange={(e) => setServiceOffering(e.target.value as ServiceOffering | "")}
              aria-invalid={"service_offering" in errorFields}
              aria-describedby={"service_offering" in errorFields ? "wiz-service-error" : undefined}
              className="h-9 rounded-md border border-input bg-background px-3 text-sm aria-invalid:border-destructive aria-invalid:ring-1 aria-invalid:ring-destructive/20"
            >
              <option value="">-- Select service --</option>
              {SERVICE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <FormError id="wiz-service-error" inline>
              {errorFields.service_offering}
            </FormError>
          </div>

          <div className="grid gap-2">
            <Label htmlFor="wiz-use-case">Primary use case *</Label>
            <Textarea
              id="wiz-use-case"
              ref={useCaseRef}
              rows={3}
              maxLength={2000}
              value={primaryUseCase}
              onChange={(e) => setPrimaryUseCase(e.target.value)}
              aria-invalid={"primary_use_case" in errorFields}
              aria-describedby={
                "primary_use_case" in errorFields ? "wiz-use-case-error" : undefined
              }
              className="w-full aria-invalid:ring-1"
              placeholder="Describe the client's primary use case for GPU infrastructure"
            />
            <FormError id="wiz-use-case-error" inline>
              {errorFields.primary_use_case}
            </FormError>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="grid gap-2">
              <Label htmlFor="wiz-tcv">Total contract value (AUD) *</Label>
              <Input
                id="wiz-tcv"
                ref={tcvRef}
                type="number"
                min="0"
                step="0.01"
                value={contractValueDollars}
                onChange={(e) => setContractValueDollars(e.target.value)}
                aria-invalid={"total_contract_value" in errorFields}
                aria-describedby={
                  "total_contract_value" in errorFields ? "wiz-tcv-error" : undefined
                }
                placeholder="100000.00"
              />
              <FormError id="wiz-tcv-error" inline>
                {errorFields.total_contract_value}
              </FormError>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="wiz-term">Proposed term *</Label>
              <select
                id="wiz-term"
                ref={termRef}
                value={termMonths}
                onChange={(e) => setTermMonths(e.target.value)}
                aria-invalid={"proposed_term_months" in errorFields}
                aria-describedby={
                  "proposed_term_months" in errorFields ? "wiz-term-error" : undefined
                }
                className="h-9 rounded-md border border-input bg-background px-3 text-sm aria-invalid:border-destructive aria-invalid:ring-1 aria-invalid:ring-destructive/20"
              >
                <option value="">-- Select term --</option>
                {TERM_OPTIONS.map((m) => (
                  <option key={m} value={m}>
                    {m} months
                  </option>
                ))}
              </select>
              <FormError id="wiz-term-error" inline>
                {errorFields.proposed_term_months}
              </FormError>
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="grid gap-2">
              <Label htmlFor="wiz-close-date">Target close date *</Label>
              {/* CHP-396: whole-row click opens the picker. */}
              <DateRowInput
                id="wiz-close-date"
                ref={closeDateRef}
                value={targetCloseDate}
                onChange={(e) => setTargetCloseDate(e.target.value)}
                aria-invalid={"target_close_date" in errorFields}
                aria-describedby={
                  "target_close_date" in errorFields ? "wiz-close-date-error" : undefined
                }
                wrapperClassName={
                  "target_close_date" in errorFields
                    ? "border-destructive ring-1 ring-destructive/20"
                    : undefined
                }
              />
              <FormError id="wiz-close-date-error" inline>
                {errorFields.target_close_date}
              </FormError>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="wiz-gpu">
                Estimated GPU count{" "}
                <span className="font-normal text-muted-foreground">(optional)</span>
              </Label>
              <Input
                id="wiz-gpu"
                ref={gpuRef}
                type="number"
                min="0"
                step="1"
                value={estimatedGpuCount}
                onChange={(e) => setEstimatedGpuCount(e.target.value)}
                aria-invalid={"estimated_gpu_count" in errorFields}
                placeholder="e.g. 8"
              />
            </div>
          </div>

          <div className="grid gap-2">
            <Label htmlFor="wiz-infra-notes">
              Infrastructure notes{" "}
              <span className="font-normal text-muted-foreground">(optional)</span>
            </Label>
            <Textarea
              id="wiz-infra-notes"
              rows={3}
              maxLength={2000}
              value={infraNotes}
              onChange={(e) => setInfraNotes(e.target.value)}
              className="w-full"
              placeholder="Any specific hardware, networking or compliance requirements"
            />
          </div>

          {error ? <FormError>{error}</FormError> : null}

          <div className="flex justify-between">
            <Button type="button" variant="outline" onClick={onBack}>
              Back
            </Button>
            <Button type="submit" loading={update.isPending} loadingLabel="Saving…">
              Next
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
